import type { SpriteLibrary } from './spriteTypes';

const cache = new Map<SpriteLibrary, Record<string, string>>();
const pending = new Map<SpriteLibrary, Promise<Record<string, string>>>();

type ContentModule = { CONTENT: Record<string, string> };

const LOADERS: Record<SpriteLibrary, () => Promise<ContentModule>> = {
  'lucide': () => import('./spriteContent.lucide.generated'),
  'simple-icons': () => import('./spriteContent.simple-icons.generated'),
  'phosphor': () => import('./spriteContent.phosphor.generated'),
  'tabler': () => import('./spriteContent.tabler.generated'),
  'heroicons': () => import('./spriteContent.heroicons.generated'),
  'material': () => import('./spriteContent.material.generated'),
  'fontawesome': () => import('./spriteContent.fontawesome.generated'),
  'ionicons': () => import('./spriteContent.ionicons.generated'),
  'fluentui': () => import('./spriteContent.fluentui.generated'),
  'bootstrap': () => import('./spriteContent.bootstrap.generated'),
  'custom': () => import('./spriteContent.custom.generated'),
  'aws': () => import('./spriteContent.aws.generated'),
  'azure': () => import('./spriteContent.azure.generated'),
  'gcp': () => import('./spriteContent.gcp.generated'),
  'kubernetes': () => import('./spriteContent.kubernetes.generated'),
};

/** Load SVG content for a library. Returns cached if already loaded. */
export function loadLibraryContent(library: SpriteLibrary): Promise<Record<string, string>> {
  const cached = cache.get(library);
  if (cached) return Promise.resolve(cached);

  let p = pending.get(library);
  if (p) return p;

  p = LOADERS[library]().then(mod => {
    cache.set(library, mod.CONTENT);
    pending.delete(library);
    return mod.CONTENT;
  });
  pending.set(library, p);
  return p;
}

/** Synchronous lookup — returns content only if the library is already cached. */
export function getSvgContent(spriteId: string): string | undefined {
  const colonIdx = spriteId.indexOf(':');
  if (colonIdx === -1) return undefined;
  const lib = spriteId.slice(0, colonIdx) as SpriteLibrary;
  return cache.get(lib)?.[spriteId];
}

/** Check if a library's content is loaded. */
export function isLibraryLoaded(library: SpriteLibrary): boolean {
  return cache.has(library);
}

/** Preload all library content in the background (sequentially to avoid overwhelming). */
export async function preloadAllContent(
  onLibraryLoaded?: (library: SpriteLibrary) => void,
): Promise<void> {
  const libs = Object.keys(LOADERS) as SpriteLibrary[];
  for (const lib of libs) {
    await loadLibraryContent(lib);
    onLibraryLoaded?.(lib);
  }
}
